import type { IBatch } from './mes-batch-service';

export interface BatchRow {
	alias: string;
	name?: string | null;
	sourceId?: string | null;
	status?: string | null;
	batch: IBatch;
	errors?: any[];
}

export interface GroupedBatchRow extends BatchRow {
	isReorder: boolean;
	isOrphanReorder: boolean;
	level: number;
}

export interface BatchGroupingSort {
	active?: string;
	direction?: '' | 'asc' | 'desc';
}

type NormalizedRow = BatchRow & { rowKey: string };

export function groupBatchRows(rows: BatchRow[], sort: BatchGroupingSort, filterValue = ''): GroupedBatchRow[] {
	const normalizedRows: NormalizedRow[] = rows.map((row, index) => ({
		...row,
		sourceId: getSourceId(row),
		rowKey: row.batch.id ?? `index-${index}`,
	}));
	const knownIds = new Set(normalizedRows.map((row) => row.batch.id).filter((id) => !!id));
	const childrenBySourceId = new Map<string, NormalizedRow[]>();
	const roots: NormalizedRow[] = [];

	normalizedRows.forEach((row) => {
		if (row.sourceId && knownIds.has(row.sourceId)) {
			const children = childrenBySourceId.get(row.sourceId) ?? [];
			children.push(row);
			childrenBySourceId.set(row.sourceId, children);
		} else {
			roots.push(row);
		}
	});

	const filterText = filterValue.trim().toLowerCase();
	const visited = new Set<string>();
	const ancestors = new Set<string>();
	const buildRows = (row: NormalizedRow, level: number, forceVisible = false): GroupedBatchRow[] => {
		if (ancestors.has(row.rowKey)) {
			return [];
		}
		visited.add(row.rowKey);
		ancestors.add(row.rowKey);
		const showSubtree = forceVisible || !filterText || matchesFilter(row, filterText);
		const children = sortRows(row.batch.id ? (childrenBySourceId.get(row.batch.id) ?? []) : [], sort).flatMap((child) => buildRows(child, level + 1, showSubtree));
		ancestors.delete(row.rowKey);
		return !showSubtree && !children.length ? [] : [toGroupedRow(row, level), ...children];
	};

	const groupedRows = sortRows(roots, sort).flatMap((row) => buildRows(row, 0));
	const leftovers = normalizedRows.filter((row) => !visited.has(row.rowKey) && (!filterText || matchesFilter(row, filterText)));
	return [...groupedRows, ...sortRows(leftovers, sort).map((row) => toGroupedRow(row, 0))];
}

export function getSourceId(row: BatchRow | IBatch | null | undefined): string | undefined {
	const sourceId = row && 'batch' in row ? row.sourceId : row?.sourceId;
	const normalizedSourceId = sourceId?.trim();
	return normalizedSourceId || undefined;
}

export function isReorderBatch(batch: IBatch | null | undefined): boolean {
	return !!getSourceId(batch);
}

function toGroupedRow(row: NormalizedRow, level: number): GroupedBatchRow {
	const { rowKey: _rowKey, ...batchRow } = row;
	const isReorder = isReorderBatch(row.batch);
	return {
		...batchRow,
		isReorder,
		isOrphanReorder: isReorder && level === 0,
		level,
	};
}

function sortRows<T extends BatchRow>(rows: T[], sort: BatchGroupingSort): T[] {
	const direction = sort?.direction === 'desc' ? -1 : 1;
	const active = sort?.active || 'alias';
	return [...rows].sort(
		(a, b) =>
			getSortValue(a, active).localeCompare(getSortValue(b, active), undefined, {
				numeric: true,
				sensitivity: 'base',
			}) * direction,
	);
}

function getSortValue(row: BatchRow, active: string): string {
	if (active === 'name') {
		return `${row.name ?? ''} ${row.alias ?? ''}`;
	}
	if (active === 'status') {
		return `${row.status ?? ''} ${row.alias ?? ''}`;
	}
	return row.alias ?? '';
}

function matchesFilter(row: BatchRow, filterText: string): boolean {
	if (!filterText) {
		return true;
	}
	return [row.alias, row.name, row.status, row.sourceId, row.batch.alias, row.batch.name, row.batch.status, row.batch.sourceId].filter((value) => value !== undefined && value !== null).some((value) => `${value}`.toLowerCase().includes(filterText));
}
